import { useCallback, useRef } from "react";
import noop from "lodash/noop";

import { useGetAccountsQuery } from "../../../../services";
import { getTransactionObject, PanelAction } from "../../../Transactions";
import {
  useTransactionDetails,
  useTransactionPanel,
  useTransactionsActions,
} from "../../../Transactions/hooks";
import { RecurringTransactionsResponse } from "../../../../services/recurringTransactions/types";
import { UseExpensePanelProps, UseExpensePanelReturn } from "./types";

const NO_RECURRING: RecurringTransactionsResponse[] = [];

// The savings row lacks cashbackEarned, so the full transaction is read before the panel opens.
export const useExpensePanel = ({
  refetchSaving,
}: UseExpensePanelProps): UseExpensePanelReturn => {
  const { data: accounts = [] } = useGetAccountsQuery();
  const fetchTransaction = useTransactionDetails();
  const { panel, openPanel, closePanel } = useTransactionPanel();
  const isFetching = useRef(false);

  const { handleSubmit } = useTransactionsActions({
    accounts,
    panel,
    selectedTransaction: null,
    recurringTransactions: NO_RECURRING,
    closePanel,
    closeDeleteModal: noop,
    requestAccount: () => void refetchSaving(),
  });

  const openExpensePanel = useCallback(
    async (action: PanelAction, transactionId: number) => {
      if (isFetching.current) {
        return;
      }
      isFetching.current = true;
      try {
        const transaction = await fetchTransaction(transactionId);
        openPanel(action, getTransactionObject(transaction));
      } catch {
        // Global middleware shows the toast for query failures.
      } finally {
        isFetching.current = false;
      }
    },
    [fetchTransaction, openPanel],
  );

  return { panel, openExpensePanel, closePanel, handleSubmit };
};
